import { useLazyQuery, useQuery } from "@apollo/client";
import {
  Close,
  FacebookOutlined,
  Instagram,
  LinkedIn,
  Twitter,
} from "@mui/icons-material";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Fab,
  Grid,
  IconButton,
  Skeleton,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Box } from "@mui/system";

import Image from "next/image";
import Link from "next/link";
import React, { useState } from "react";

import ContactConciergeForm from "components/shared/ContactConciergeForm";
import {
  MEDIA_TOOLKIT_LINK_QUERY,
  MEDIA_TOOLKIT_QUERY,
} from "graphql/media-toolkit/queries";
import useCommunity from "hooks/useCommunity";
import useFlags from "hooks/useFlags";
import useIdentity from "hooks/useIdentity";

import useStyles from "./styles";

type MediaCardProps = {
  buttonText: string;
  description: string | JSX.Element;
  onClick?: () => void;
  pendoClass: string;
  title: string;
  type?: string;
};

function getToolkitImage({ items, type }) {
  return items.find((image) => image.type === type)?.coverImage || null;
}

function MediaCardSkeleton() {
  return (
    <Grid item md={4} sm={6} xs={12}>
      <Card>
        <Skeleton height="164px" variant="rectangular" />
        <CardContent sx={{ paddingBottom: 0 }}>
          <Skeleton sx={{ typography: "h3", mb: 2 }} variant="text" />
          <Skeleton sx={{ typography: "body2" }} variant="text" />
          <Skeleton sx={{ typography: "body2" }} variant="text" />
          <Skeleton sx={{ typography: "body2" }} variant="text" />
        </CardContent>
        <CardActions sx={{ marginTop: "auto", p: 2, paddingTop: "24px" }}>
          <Skeleton height="40px" variant="rounded" width="100%" />
        </CardActions>
      </Card>
    </Grid>
  );
}

function MediaCard({
  buttonText,
  description,
  onClick,
  pendoClass,
  title,
  type,
}: MediaCardProps) {
  const { data } = useQuery(MEDIA_TOOLKIT_QUERY);
  const mediaToolkitItems = data?.mediaToolkit?.items || [];
  const shownMediaToolkitItems = mediaToolkitItems.map((item) => item.type);

  if (!shownMediaToolkitItems.includes(type)) {
    return null;
  }

  const image = getToolkitImage({ items: mediaToolkitItems, type });

  return (
    <Grid item md={4} sm={6} xs={12}>
      <Card sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box height="164px" position="relative">
          {image && (
            <Image
              alt={title}
              height={164}
              layout="fill"
              objectFit="cover"
              src={image}
              width={293}
            />
          )}
        </Box>
        <CardContent sx={{ paddingBottom: 0 }}>
          <Typography sx={{ mb: 2 }} variant="body1Semibold">
            {title}
          </Typography>
          <Typography variant="body2">{description}</Typography>
        </CardContent>
        <CardActions sx={{ marginTop: "auto", p: 2, paddingTop: "24px" }}>
          <Button
            fullWidth
            className={pendoClass}
            variant="contained"
            onClick={onClick}
          >
            {buttonText}
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );
}

function SocialSkeleton() {
  const isMdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  return (
    <>
      <Skeleton sx={{ mb: 3, typography: "body2" }} variant="text" />

      <Box display="flex" gap={2} mb={{ xs: 2.5, md: 3 }}>
        <Box height="117px" minWidth="117px" position="relative" width="117px">
          <Skeleton height="117px" variant="rectangular" width="117px" />
        </Box>

        <Box display="flex" flexDirection="column" width="100%">
          <Skeleton
            sx={{ mb: 1, typography: "subtitle1" }}
            variant="text"
            width="250px"
          />
          <Skeleton sx={{ mb: 2, typography: "body2" }} variant="text" />
          {isMdUp && (
            <Box display="flex" gap={3}>
              <Skeleton height="32px" variant="rounded" width="200px" />
              <Skeleton height="32px" variant="rounded" width="200px" />
            </Box>
          )}
        </Box>
      </Box>
      {!isMdUp && (
        <Box display="flex" flexDirection="column" gap={3} mb={3}>
          <Skeleton height="32px" variant="rounded" width="100%" />
          <Skeleton height="32px" variant="rounded" width="100%" />
        </Box>
      )}
    </>
  );
}

type SocialBoxProps = {
  children: JSX.Element[];
  Icon: React.ElementType;
  iconText: string;
};

function SocialBox({ children, Icon, iconText }: SocialBoxProps) {
  const classes = useStyles();

  return (
    <Box
      display="flex"
      flexDirection={{ xs: "column", md: "row" }}
      minHeight="88px"
      px={{ xs: 1, md: 2 }}
      py={2}
      sx={{ backgroundColor: "grey.100", borderRadius: "4px" }}
    >
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        mb={{ xs: 2, md: 0 }}
        minWidth="140px"
      >
        <Icon sx={{ fontSize: "30px" }} />
        <Typography variant="subtitle1">{iconText}</Typography>
      </Box>
      <ul className={classes.list}>{children}</ul>
    </Box>
  );
}

async function downloadFile(url) {
  const urlObject = new URL(url);
  const fileName = urlObject.pathname.split("/").pop();
  try {
    const response = await fetch(url);

    if (response.status !== 200) {
      throw new Error(
        `Unable to download file. HTTP status: ${response.status}`
      );
    }

    const blob = await response.blob();

    const downloadLink = document.createElement("a");
    downloadLink.style.display = "none";
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = fileName;

    document.body.appendChild(downloadLink);
    downloadLink.click();

    setTimeout(() => {
      URL.revokeObjectURL(downloadLink.href);
      document.body.removeChild(downloadLink);
    }, 100);
  } catch (error) {
    console.error("Error downloading the file:", error.message);
  }
}

const downloadTypes = [
  "ALL",
  "BADGES",
  "EMAIL_SIGNATURES",
  "PR_TEMPLATE",
  "SOCIAL_GRAPHICS",
  "SOCIAL_GUIDE",
];

const linkTypes = ["HEADSHOTS", "PERSONALIZED_TEMPLATES", "PR_WEB"];

export default function MediaToolkit() {
  const classes = useStyles();

  const { communityCode } = useIdentity();
  const { community } = useCommunity({
    code: communityCode,
  });
  const { permMediaToolkitSections = [] } = useFlags();

  const {
    socialMediaLinks: { facebook, instagram, linkedin, twitter },
  } = community || { socialMediaLinks: {} };

  const [guidelinesDialogOpen, setGuidelinesDialogOpen] = useState(false);
  const [contactConciergeDialogOpen, setContactConciergeDialogOpen] =
    useState(false);

  const [getMediaToolkitLink] = useLazyQuery(MEDIA_TOOLKIT_LINK_QUERY);
  const { data, loading } = useQuery(MEDIA_TOOLKIT_QUERY);
  const mediaToolkitItems = data?.mediaToolkit?.items || [];
  const shownMediaToolkitItems = mediaToolkitItems.map((item) => item.type);

  const isSmToLg = useMediaQuery((theme: Theme) =>
    theme.breakpoints.between("sm", "xl")
  );
  const isMdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  async function handleMediaClick(type) {
    const mediaToolkitLinkData = await getMediaToolkitLink({
      variables: { type },
    });

    const mediaToolkitLink = mediaToolkitLinkData?.data?.mediaToolkitLink;

    if (mediaToolkitLink) {
      if (downloadTypes.includes(type)) {
        await downloadFile(mediaToolkitLink);
        return;
      }
      if (linkTypes.includes(type)) {
        window.open(mediaToolkitLink, "_blank");
      }
    }
  }

  const personalizedCardText =
    "Find and download your custom illustrated headshot here. It's yours to keep!";
  let personalizedCardTitle = "Branded templates";
  let personalizedCardButtonText = "Use template";

  if (isSmToLg) {
    [personalizedCardTitle] = personalizedCardTitle.split(" ");
    [personalizedCardButtonText] = personalizedCardButtonText.split(" ");
  }

  const showJumpLinks = permMediaToolkitSections.length > 1;

  return (
    <>
      <Box mt={2.5}>
        <Typography sx={{ mb: 2 }} variant="h2">
          Media toolkit
        </Typography>
        <Typography sx={{ mb: 3 }} variant="body2">
          Discover simple and creative ways to announce your membership using
          the materials included in our exclusive media toolkit.
        </Typography>
        {showJumpLinks && (
          <>
            <Typography sx={{ mb: 2 }} variant="subtitle1">
              On this page
            </Typography>
            <ul className={classes.list}>
              {permMediaToolkitSections.includes("announce") && (
                <li>
                  <a href="#announce-membership">Announce your membership</a>
                </li>
              )}
              {permMediaToolkitSections.includes("social") && (
                <li>
                  <a href="#social-media">Social media</a>
                </li>
              )}
              {permMediaToolkitSections.includes("support") && (
                <li>
                  <a href="#additional-support">Additional support</a>
                </li>
              )}
            </ul>
          </>
        )}

        {(shownMediaToolkitItems.includes("ALL") ||
          shownMediaToolkitItems.includes("SOCIAL_GUIDE")) && (
          <Box
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            gap={{ xs: 2, md: 3 }}
            sx={{ mt: 3 }}
          >
            {shownMediaToolkitItems.includes("ALL") && (
              <Box display="flex" flexDirection="column">
                <Button
                  className="pendo_desktop-mediatoolkit-downloadallassets"
                  sx={{ mb: 0.5 }}
                  variant="contained"
                  onClick={() => handleMediaClick("ALL")}
                >
                  Download all assets (.zip)
                </Button>
                <Typography color="textSecondary" variant="caption">
                  Download all materials in a single .zip file
                </Typography>
              </Box>
            )}
            {shownMediaToolkitItems.includes("SOCIAL_GUIDE") && (
              <Box display="flex" flexDirection="column">
                <Button
                  className="pendo_desktop-mediatoolkit-socialmediaguide"
                  sx={{ mb: 0.5 }}
                  variant="contained"
                  onClick={() => handleMediaClick("SOCIAL_GUIDE")}
                >
                  Download social media guide
                </Button>
                <Typography color="textSecondary" variant="caption">
                  See more info or share with your team
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {permMediaToolkitSections.includes("announce") && (
          <>
            <Divider sx={{ my: 6 }} />

            <Typography id="announce-membership" sx={{ mb: 2 }} variant="h2">
              Announce your membership
            </Typography>
            <Typography sx={{ mb: 3 }} variant="body2">
              Ready-to-post assets and resources are available to help you
              announce your membership to your personal and professional
              networks.
            </Typography>

            <Grid container spacing={3}>
              {loading ? (
                <>
                  <MediaCardSkeleton />
                  <MediaCardSkeleton />
                  <MediaCardSkeleton />
                </>
              ) : (
                <>
                  <MediaCard
                    buttonText={isSmToLg ? "Download" : "Download badges"}
                    description="With ready-to-use assets, establish your membership, authority, and credibility within the community."
                    pendoClass="pendo_desktop-mediatoolkit-downloadbadges"
                    title="Badges"
                    type="BADGES"
                    onClick={() => handleMediaClick("BADGES")}
                  />
                  <MediaCard
                    buttonText={personalizedCardButtonText}
                    description={
                      <>
                        Check out the template below for a branded announcement.
                        Please follow these brand integrity{" "}
                        <Typography
                          color="secondary"
                          component="span"
                          sx={{ cursor: "pointer" }}
                          variant="body2"
                          onClick={() => setGuidelinesDialogOpen(true)}
                        >
                          guidelines
                        </Typography>
                        .
                      </>
                    }
                    pendoClass="pendo_desktop-mediatoolkit-customizetemplate"
                    title={personalizedCardTitle}
                    type="PERSONALIZED_TEMPLATES"
                    onClick={() => handleMediaClick("PERSONALIZED_TEMPLATES")}
                  />
                  <MediaCard
                    buttonText={
                      isSmToLg ? "Redeem code" : "Redeem discount code"
                    }
                    description="Upload 25 of your personal photos and Secta.ai’s artificial intelligence generates hundreds of professional headshots in under an hour."
                    pendoClass="pendo_desktop-mediatoolkit-sectaaicode"
                    title={isSmToLg ? "Secta.ai" : "Secta.ai headshots"}
                    type="HEADSHOTS"
                    onClick={() => handleMediaClick("HEADSHOTS")}
                  />
                  <MediaCard
                    buttonText={isSmToLg ? "Download" : "Download images"}
                    description="Let everyone know you're an Official Member by attaching these images to your email signature."
                    pendoClass="pendo_desktop-mediatoolkit-emailsignature"
                    title="Email signature"
                    type="EMAIL_SIGNATURES"
                    onClick={() => handleMediaClick("EMAIL_SIGNATURES")}
                  />
                  <MediaCard
                    buttonText={isSmToLg ? "Download" : "Download template"}
                    description={
                      <>
                        Send a press release announcing your membership.
                        It&apos;s simple to do using our{" "}
                        <b>press release template</b> and PRWeb partnership.
                      </>
                    }
                    pendoClass="pendo_desktop-mediatoolkit-pressreleasetemplate"
                    title="Share the news"
                    type="PR_TEMPLATE"
                    onClick={() => handleMediaClick("PR_TEMPLATE")}
                  />
                  <MediaCard
                    buttonText={
                      isSmToLg ? "Claim discount" : "Claim your discount"
                    }
                    description={
                      <>
                        Get 25% off a press release through Cision Distribution
                        by PRWeb with code <b>EXEC2023</b> with your
                        complimentary EXEC membership.
                      </>
                    }
                    pendoClass="pendo_desktop-mediatoolkit-pressreleasepackages"
                    title={
                      isSmToLg ? "Press release" : "Press release template"
                    }
                    type="PR_WEB"
                    onClick={() => handleMediaClick("PR_WEB")}
                  />
                </>
              )}
            </Grid>
          </>
        )}

        {permMediaToolkitSections.includes("social") && (
          <>
            <Divider sx={{ my: 6 }} />

            <Typography id="social-media" sx={{ mb: 2 }} variant="h2">
              Social media
            </Typography>
            {loading ? (
              <SocialSkeleton />
            ) : (
              (shownMediaToolkitItems.includes("SOCIAL_GRAPHICS") ||
                shownMediaToolkitItems.includes("SOCIAL_GUIDE")) && (
                <>
                  <Typography sx={{ mb: 3 }} variant="body2">
                    This social media pack includes everything you need to
                    showcase your membership across your favorite social
                    platforms.
                  </Typography>
                  <Box display="flex" gap={2} mb={{ xs: 2.5, md: 3 }}>
                    <Box
                      height="117px"
                      minWidth="117px"
                      position="relative"
                      width="117px"
                    >
                      {getToolkitImage({
                        items: mediaToolkitItems,
                        type: "SOCIAL_GRAPHICS",
                      }) && (
                        <Image
                          layout="fill"
                          objectFit="cover"
                          src={getToolkitImage({
                            items: mediaToolkitItems,
                            type: "SOCIAL_GRAPHICS",
                          })}
                        />
                      )}
                    </Box>

                    <Box display="flex" flexDirection="column">
                      <Typography sx={{ mb: 1 }} variant="subtitle1">
                        Social media graphics
                      </Typography>
                      <Typography sx={{ mb: 2 }} variant="body2">
                        Let everyone know you&apos;re part of our exclusive
                        community with ready-to-post social graphics.
                      </Typography>
                      {isMdUp && (
                        <Box display="flex" gap={3}>
                          {shownMediaToolkitItems.includes(
                            "SOCIAL_GRAPHICS"
                          ) && (
                            <Button
                              className="pendo_desktop-mediatoolkit-socialmediagraphics"
                              variant="contained"
                              onClick={() =>
                                handleMediaClick("SOCIAL_GRAPHICS")
                              }
                            >
                              Download graphics
                            </Button>
                          )}
                          {shownMediaToolkitItems.includes("SOCIAL_GUIDE") && (
                            <Button
                              className="pendo_desktop-mediatoolkit-socialmediaguide"
                              variant="contained"
                              onClick={() => handleMediaClick("SOCIAL_GUIDE")}
                            >
                              Download social media guide
                            </Button>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                  {!isMdUp && (
                    <Box display="flex" flexDirection="column" gap={3} mb={3}>
                      {shownMediaToolkitItems.includes("SOCIAL_GRAPHICS") && (
                        <Button
                          className="pendo_desktop-mediatoolkit-socialmediagraphics"
                          variant="contained"
                          onClick={() => handleMediaClick("SOCIAL_GRAPHICS")}
                        >
                          Download graphics
                        </Button>
                      )}
                      {shownMediaToolkitItems.includes("SOCIAL_GUIDE") && (
                        <Button
                          className="pendo_desktop-mediatoolkit-socialmediaguide"
                          variant="contained"
                          onClick={() => handleMediaClick("SOCIAL_GUIDE")}
                        >
                          Download social media guide
                        </Button>
                      )}
                    </Box>
                  )}
                </>
              )
            )}

            <Typography sx={{ mb: 2 }} variant="h3">
              Connect on social
            </Typography>

            <Box mb={2}>
              <ul className={classes.list}>
                <li>
                  Add your social media links to your{" "}
                  <Link href="/profile">
                    <a>member profile</a>
                  </Link>{" "}
                  so we can follow and tag you in our posts.
                </li>
                <li>
                  Highlight published content on your social media accounts —
                  don’t forget to tag us so we can engage with your posts!
                </li>
                <li>
                  We feature member&apos;s work regularly on our social pages,
                  so make sure to click below and follow us so you never miss a
                  beat.
                </li>
              </ul>
            </Box>

            {(linkedin || facebook || twitter || instagram) && (
              <Box display="flex" gap={3} mb={3}>
                {twitter && (
                  <Fab
                    color="primary"
                    href={twitter}
                    size="medium"
                    target="_blank"
                  >
                    <Twitter sx={{ fontSize: "30px" }} />
                  </Fab>
                )}
                {instagram && (
                  <Fab
                    color="primary"
                    href={instagram}
                    size="medium"
                    target="_blank"
                  >
                    <Instagram sx={{ fontSize: "30px" }} />
                  </Fab>
                )}
                {facebook && (
                  <Fab
                    color="primary"
                    href={facebook}
                    size="medium"
                    target="_blank"
                  >
                    <FacebookOutlined sx={{ fontSize: "30px" }} />
                  </Fab>
                )}
                {linkedin && (
                  <Fab
                    color="primary"
                    href={linkedin}
                    size="medium"
                    target="_blank"
                  >
                    <LinkedIn sx={{ fontSize: "30px" }} />
                  </Fab>
                )}
              </Box>
            )}

            <Typography sx={{ mb: 2 }} variant="h3">
              Social media 101
            </Typography>

            <Box display="flex" flexDirection="column" gap={2}>
              <SocialBox Icon={Twitter} iconText="Twitter">
                <li>Keep posts short and sweet.</li>
                <li>Use 2-3 relevant hashtags to increase visibility.</li>
                <li>Incorporate visual elements like photos and videos.</li>
              </SocialBox>
              <SocialBox Icon={Instagram} iconText="Instagram">
                <li> Use high-quality images and videos.</li>
                <li>
                  Use Instagram Stories and Reels to showcase behind-the-scenes
                  content.
                </li>
                <li>Use 3-5 hashtags to increase visibility.</li>
              </SocialBox>
              <SocialBox Icon={FacebookOutlined} iconText="Facebook">
                <li>Use a conversational tone.</li>
                <li>Incorporate visual elements like photos and videos. </li>
              </SocialBox>
              <SocialBox Icon={LinkedIn} iconText="LinkedIn">
                <li>Keep posts informative and relevant to your industry.</li>
                <li>
                  Post links to your thought leadership content and invite your
                  network to join the conversation.
                </li>
                <li>Add visual interest with images and videos.</li>
                <li>
                  Highlight your achievement as an Official Member in the
                  “Experience” section of your profile.
                </li>
              </SocialBox>
            </Box>
          </>
        )}

        {permMediaToolkitSections.includes("support") && (
          <>
            <Divider sx={{ my: 6 }} />

            <Typography id="additional-support" sx={{ mb: 3 }} variant="h2">
              Additional support
            </Typography>

            <Typography sx={{ mb: 3 }} variant="body2">
              If you have any questions or need additional assistance in
              promoting your membership, please don&apos;t hesitate to{" "}
              <Typography
                color="secondary"
                component="span"
                sx={{ cursor: "pointer" }}
                variant="body2"
                onClick={() => setContactConciergeDialogOpen(true)}
              >
                contact us
              </Typography>
              . We&apos;re here to help you every step of the way.
            </Typography>
            <Typography variant="body2">
              Thank you for being a valued member of our community!
            </Typography>
          </>
        )}
      </Box>

      <Dialog
        open={guidelinesDialogOpen}
        onClose={() => setGuidelinesDialogOpen(false)}
      >
        <DialogTitle sx={{ m: 0, px: 3, py: 2.5 }}>
          Brand guidelines
          <IconButton
            aria-label="close"
            size="large"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
            onClick={() => setGuidelinesDialogOpen(false)}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 4, pb: 4 }}>
          <ul className={classes.list}>
            <li>
              Change only the text in the name, title, and company fields. Do
              not change the font, font sizes, or color.
            </li>
            <li>
              Use only the title and company provided at the time your
              application was approved.
            </li>
            <li>
              Upload your headshot and drag and drop it into the provided photo
              frame. Don’t add your own frames or change the shape of the
              existing frame.
            </li>
            <li>
              Make sure the photo you use is high-resolution and professional in
              nature.
            </li>
            <li>Don’t edit or move any locked design elements.</li>
            <li>
              Do not add any additional information to the graphic. It should
              simply include your headshot, name, title, and company.
            </li>
          </ul>
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth
        open={contactConciergeDialogOpen}
        onClose={() => setContactConciergeDialogOpen(false)}
      >
        <DialogTitle sx={{ m: 0, px: 3, py: 2.5 }}>
          We&apos;re here to help
          <IconButton
            aria-label="close"
            size="large"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
            onClick={() => setContactConciergeDialogOpen(false)}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 4, pb: 4 }}>
          <Typography sx={{ mb: 2 }}>
            Send us an email and a member of our team will be in touch shortly.
          </Typography>
          <ContactConciergeForm
            body="Hi, I have questions about the media toolkit."
            handleCancel={() => setContactConciergeDialogOpen(false)}
            handleRequested={() => setContactConciergeDialogOpen(false)}
            subject="Help me use my media toolkit"
            submitButtonText="Contact us"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
